// getUserProfileJsonLd.js
import getProfileTdk from './getProfileTdk.js';
import {
  getPerson,
  getFlixVideoObject,
  getFlixPreviewVideoObject,
  getInteractionCounter,
  getStoryVideoObject,
  getStoryPreviewVideoObject,
  getShortSchemaObject,
  getPostImageObject,
} from './SchemaObject.js';
import {
  getPoster,
  getAssetManifest,
  MediaAssetFormat,
  MediaPreset,
} from './getMediaAsset.js';
import { ProfileListType } from './profileConstants.js';
import { EMPTY_ARRAY } from './defaults.js';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';
import getMessageDataList from '../selector/getMessageDataList.js';
import getListData from '../selector/getListData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import {
  OUTBOX_FEED_STORY,
  OUTBOX_FEED_POST_VIDEO,
  OUTBOX_FEED_POST_IMAGE,
  OUTBOX_FEED_SHORT,
  USER_PROFILE_TABS,
} from '../RemoteConfigKeys.js';
import { getFeedNameType, sanitizeFeedQuery } from './feedUtils.js';
import { DEFAULT_LANGUAGE } from './i18nCommon.js';
import { TranslationNamespace } from './translationNamespace.js';
import { normalizeLang, getHreflang } from './langPath.js';

/**
 * Generate JSON-LD data for user profile page
 * @param {Object} params
 * @param {Object} params.state - Redux state
 * @param {string} params.userId - User ID
 * @param {string} params.username - Username
 * @param {string} params.biography - User biography
 * @param {string} params.type - Profile list type (story, flix, image, short)
 * @param {Function} params.t - Translation function
 * @param {Object} params.i18n - i18n instance (required)
 * @returns {Object} JSON-LD data object
 */
export const getUserProfileJsonLd = async ({
  state,
  userId,
  username = '',
  biography = '',
  type = '',
  t,
  i18n,
}) => {
  const currentLanguage = i18n.language;
  const langPath =
    normalizeLang(currentLanguage) === DEFAULT_LANGUAGE
      ? ''
      : `/${normalizeLang(currentLanguage)}`;
  const base = getUserProfileUrl({ userId, username });
  const userProfileUrl = `https://swag.live${langPath}${base}`;
  const postPreviewUrl = `https://swag.live${langPath}/post`;

  const normalizedInLanguage = getHreflang(currentLanguage);

  // Get message data lists for different feed types
  const [storyDataList, flixDataList, imageDataList, shortsDataList] = [
    OUTBOX_FEED_STORY,
    OUTBOX_FEED_POST_VIDEO,
    OUTBOX_FEED_POST_IMAGE,
    OUTBOX_FEED_SHORT,
  ].map(item => {
    let category = getRemoteConfigData(state, item, undefined, {
      USER_ID: userId,
    });
    category = sanitizeFeedQuery({ feedName: category });
    const ids =
      getListData(
        state,
        ['home', getFeedNameType({ feedName: category }), category],
        'itemIds'
      ) || [];
    const messageDataList = getMessageDataList(state, ids);
    return messageDataList;
  });

  const loadLanguagePromise = async () => {
    if (!i18n.hasResourceBundle(currentLanguage, TranslationNamespace.SEO)) {
      await i18n.reloadResources(currentLanguage, TranslationNamespace.SEO);
    }
  };
  await loadLanguagePromise();

  const tabs = getRemoteConfigData(state, USER_PROFILE_TABS) || EMPTY_ARRAY;
  const isFirstTabActived = type === tabs[0];

  const buildNameValueByLang = (lang, translateFn) => translateFn(lang);

  const getProfileTdkByLang = (lang, listTypeOption = type) => {
    const t = (key, options = {}) => i18n.t(key, { ...options, lng: lang });
    return getProfileTdk({
      username,
      biography,
      listType: listTypeOption,
      t,
    });
  };

  const getProfileTdkNameByLang = (lang, listTypeOption = type) =>
    getProfileTdkByLang(lang, listTypeOption).displayTitle;
  const getProfileTdkNameValueByLang = (lang, listTypeOption = type) =>
    buildNameValueByLang(lang, l => getProfileTdkNameByLang(l, listTypeOption));

  const getProfileTdkDescriptionByLang = (lang, listTypeOption = type) =>
    getProfileTdkByLang(lang, listTypeOption).displayDescription;
  const getProfileTdkDescriptionValueByLang = (lang, listTypeOption = type) =>
    buildNameValueByLang(lang, l =>
      getProfileTdkDescriptionByLang(l, listTypeOption)
    );

  // Ensure userProfileUrl has no trailing slash for consistent @id references
  const canonicalProfileUrl = userProfileUrl.replace(/\/$/, '');

  const createOfferCatalog = (id, url, dataList) => {
    // If no data, return undefined to avoid empty OfferCatalog
    if (!dataList || dataList.length === 0) return undefined;

    return {
      '@type': 'OfferCatalog',
      '@id': id,
      url,
      itemListElement: dataList.map((messageData, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': `${postPreviewUrl}/${messageData.id}#preview`,
        },
      })),
    };
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [
      getPerson({
        id: userId,
        username,
        biography,
        hasOfferCatalog: [
          {
            '@id': `${userProfileUrl}/story#stories`,
            url: `${userProfileUrl}/story`,
          },
          {
            '@id': `${userProfileUrl}/flix#flixes`,
            url: `${userProfileUrl}/flix`,
          },
          {
            '@id': `${userProfileUrl}/short#shorts`,
            url: `${userProfileUrl}/short`,
          },
          {
            '@id': `${userProfileUrl}/image#images`,
            url: `${userProfileUrl}/image`,
          },
        ],
        origin: `https://swag.live${langPath}`,
      }),
      {
        '@type': 'ProfilePage',
        '@id': `${canonicalProfileUrl}${type ? `/${type}` : ''}#webpage`,
        url: `${userProfileUrl}${type ? `/${type}` : ''}`,
        inLanguage: normalizedInLanguage,
        name: getProfileTdkNameValueByLang(currentLanguage),
        description: getProfileTdkDescriptionValueByLang(currentLanguage),
        breadcrumb: {
          '@id': `${canonicalProfileUrl}#breadcrumb`,
        },
        mainEntity: {
          '@id': `${userProfileUrl}#person`,
        },
        significantLinks: tabs.map((tab, index) => {
          if (index === 0) {
            return `${userProfileUrl}`;
          }
          return `${userProfileUrl}/${tab}`;
        }),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonicalProfileUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: buildNameValueByLang(currentLanguage, lang =>
              t('seo-home-title', {
                ns: TranslationNamespace.SEO,
                lng: lang,
              })
            ),
            item: 'https://swag.live',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: getProfileTdk({
              username,
              biography,
              listType: tabs[0],
              t: (key, options = {}) =>
                i18n.t(key, { ...options, lng: currentLanguage }),
            }).displayTitle,
            item: `${userProfileUrl}`,
          },
          !isFirstTabActived && {
            '@type': 'ListItem',
            position: 3,
            name: getProfileTdkNameValueByLang(currentLanguage),
            item: `${userProfileUrl}/${type}`,
          },
        ].filter(a => a),
      },
      createOfferCatalog(
        `${canonicalProfileUrl}#person`,
        `${canonicalProfileUrl}`,
        [...storyDataList, ...flixDataList, ...shortsDataList, ...imageDataList]
      ),
      createOfferCatalog(
        `${canonicalProfileUrl}/${ProfileListType.STORY}#stories`,
        `${canonicalProfileUrl}/${ProfileListType.STORY}`,
        storyDataList
      ),
      createOfferCatalog(
        `${canonicalProfileUrl}/${ProfileListType.FLIX}#flixes`,
        `${canonicalProfileUrl}/${ProfileListType.FLIX}`,
        flixDataList
      ),
      createOfferCatalog(
        `${canonicalProfileUrl}/${ProfileListType.SHORT}#shorts`,
        `${canonicalProfileUrl}/${ProfileListType.SHORT}`,
        shortsDataList
      ),
      createOfferCatalog(
        `${canonicalProfileUrl}/${ProfileListType.IMAGE}#images`,
        `${canonicalProfileUrl}/${ProfileListType.IMAGE}`,
        imageDataList
      ),
      ...storyDataList.map(messageData => {
        return getStoryPreviewVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          username,
          title: messageData.title,
          caption: messageData.caption,
          durationSecs: messageData.media?.duration,
          langPath,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
        });
      }),
      ...storyDataList.map(messageData => {
        return getStoryVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          username,
          title: messageData.title,
          caption: messageData.caption,
          durationSecs: messageData.media?.duration,
          langPath,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
          isFree: !messageData.unlockPrice,
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
      ...flixDataList.map(messageData => {
        return getFlixPreviewVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: 10,
          langPath,
          postedAtUnix: messageData.postedAtUnix,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
        });
      }),
      ...flixDataList.map(messageData => {
        return getFlixVideoObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          langPath,
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: messageData.media?.duration || 600,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
      ...shortsDataList.map(messageData => {
        const firstAssetId = messageData?.assetIds?.[0];
        return getShortSchemaObject({
          id: messageData.id,
          title: messageData.title,
          description: messageData.caption || messageData.title,
          durationSecs: messageData.media?.duration,
          langPath,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
          username,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ],
          encodings: [
            {
              '@id': `https://swag.live${langPath}/shorts/${messageData.id}#video-dash`,
            },
          ],
          associatedMedia: messageData.sourceId
            ? {
                '@id': `${postPreviewUrl}/${messageData.sourceId}#preview`,
              }
            : undefined,
          thumbnailUrl: getPoster({
            id: messageData.id,
            size: '1024x1024',
            format: MediaAssetFormat.JPG,
            preset: MediaPreset.SD,
          }),
          contentUrl: firstAssetId
            ? getAssetManifest({
                id: messageData.id,
                assetId: firstAssetId,
                format: MediaAssetFormat.M3U8,
                preset: MediaPreset.SD,
              })?.href
            : undefined,
        });
      }),
      ...imageDataList.map(messageData => {
        return getPostImageObject({
          id: messageData.id,
          assetId: messageData?.assetIds?.[0],
          description: messageData.caption,
          durationSecs: messageData.media?.duration,
          langPath,
          postedAtUnix: messageData.postedAtUnix,
          isFree: !messageData.unlockPrice,
          author: {
            '@id': `${userProfileUrl}#person`,
          },
          interactionStatistic: [
            { type: 'ViewAction', count: messageData.views || 0 },
            { type: 'BuyAction', count: messageData.unlocks || 0 },
            { type: 'LikeAction', count: messageData.likes || 0 },
            {
              type: 'DislikeAction',
              count: messageData.dislikes || 0,
            },
          ]
            .filter(s => s.count)
            .map(getInteractionCounter),
        });
      }),
    ].filter(Boolean), // Filter out undefined values from createOfferCatalog
  };
};

export default getUserProfileJsonLd;
